import SectionTitle from "../common/SectionTitle";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { villaData } from "../data/villaData";
import { useNavigate } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

const PremiumVillas = ({ scrollToSection }) => {
  const navigate = useNavigate();
  return (
    <section id="premium-villas" className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionTitle
            title={villaData.section?.title || "Premium Villas"}
            subtitle={
              villaData.section?.subtitle ||
              "Thoughtfully designed modern villas for comfortable family living"
            }
          />
        </motion.div>

        {/* Slider */}
        <div className="mt-10">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={24}
            loop
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{ clickable: true, el: ".premium-villas-pagination" }}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {villaData.villas.map((villa) => (
              <SwiperSlide key={villa.id}>
                <motion.div
                  whileHover={{ y: 6 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="relative rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-lg hover:shadow-xl"
                >
                  {/* Status Badge */}
                  {villa.status === "Limited Units" && (
                    <span className="absolute top-4 left-4 z-10 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Limited Units
                    </span>
                  )}

                  {/* Image */}
                  <div className="h-56 overflow-hidden">
                    <img
                      src={villa.image}
                      alt={villa.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-bold text-xl mb-2 text-gray-900">
                      {villa.name}
                    </h3>
                    <span className="text-sm text-emerald-700 font-semibold">
                      {villa.villaType}
                    </span>

                    <p className="text-gray-600 text-sm mb-4">
                      {villa.description}
                    </p>

                    {/* Area & Price */}
                    <div className="flex items-center justify-between mb-5">
                      <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold">
                        {villa.areaSqFt} sq.ft · {villa.bhk} BHK
                      </span>

                      <span className="text-lg font-bold text-gray-900">
                        {villa.price.label}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5" />

                    {/* CTA */}
                    <div className="flex gap-4">
                      <button
                        onClick={() =>
                          navigate("/contact", {
                            state: {
                              villaType: villa.villaType,
                            },
                          })
                        }
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 py-2 text-sm font-semibold text-white
                        hover:bg-emerald-800 transition-all duration-300 shadow-md hover:shadow-lg group"
                      >
                        Book Site Visit
                        <ArrowRight
                          size={18}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </button>

                      <button
                        onClick={() => navigate(`/villa/${villa.id}`)}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-700 px-3 py-2 text-sm font-semibold text-emerald-700
                        hover:bg-emerald-50 transition-all duration-300 shadow-md hover:shadow-lg group"
                      >
                        View Villa Details
                        <ArrowRight
                          size={18}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="premium-villas-pagination mt-2 flex justify-center" />
        </div>
      </div>
    </section>
  );
};

export default PremiumVillas;
